import {
    AlertDialog,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
    AlertDialogTrigger,
} from '@/components/ui/alert-dialog'
import { Button } from '@/components/ui/button'
import { cn } from '@/lib/utils'
import { PurchaseProcessStatus } from '@Purchasing/Domain/Model/PurchaseProcess'
import { Ban, CheckCircle2, Flag, Play, XCircle } from 'lucide-react'
import { useState } from 'react'

export const allowedProcessTransitions: Record<PurchaseProcessStatus, PurchaseProcessStatus[]> = {
    [PurchaseProcessStatus.DRAFT]: [PurchaseProcessStatus.ACTIVE, PurchaseProcessStatus.CANCELED],
    [PurchaseProcessStatus.ACTIVE]: [PurchaseProcessStatus.LOST, PurchaseProcessStatus.CANCELED, PurchaseProcessStatus.FINISHED],
    [PurchaseProcessStatus.LOST]: [],
    [PurchaseProcessStatus.CANCELED]: [],
    [PurchaseProcessStatus.FINISHED]: [],
}

// Encerramentos são definitivos: cada um explica o que significa para o time.
const closingOptions = [
    {
        status: PurchaseProcessStatus.FINISHED,
        label: 'Finalizar',
        description: 'A compra foi concluída e entregue. O processo sai do fluxo ativo.',
        icon: CheckCircle2,
        tone: 'border-brand bg-accent text-brand-deep',
    },
    {
        status: PurchaseProcessStatus.LOST,
        label: 'Marcar como perdido',
        description: 'O cliente não fechou. O histórico e as cotações ficam guardados para consulta.',
        icon: XCircle,
        tone: 'border-brand-yellow bg-warning-50 text-warning-600',
    },
    {
        status: PurchaseProcessStatus.CANCELED,
        label: 'Cancelar',
        description: 'A necessidade deixou de existir ou foi aberta por engano.',
        icon: Ban,
        tone: 'border-brand-pink bg-od-secondary-50 text-od-secondary-600',
    },
] as const

export const ProcessStatusControl = ({ status, busy, onChange }: {
    status: PurchaseProcessStatus
    busy: boolean
    onChange: (next: PurchaseProcessStatus) => Promise<void>
}) => {
    const [open, setOpen] = useState(false)
    const [choice, setChoice] = useState<PurchaseProcessStatus | null>(null)
    const allowed = allowedProcessTransitions[status] ?? []
    if (!allowed.length) return null

    const closings = closingOptions.filter((option) => allowed.includes(option.status))
    const selected = closings.find((option) => option.status === choice)

    const confirm = async () => {
        if (!choice) return
        await onChange(choice)
        setOpen(false)
        setChoice(null)
    }

    return (
        <div className="flex flex-wrap items-center gap-2">
            {allowed.includes(PurchaseProcessStatus.ACTIVE) && (
                <Button type="button" size="sm" className="gap-1" disabled={busy} onClick={() => void onChange(PurchaseProcessStatus.ACTIVE)}>
                    <Play className="h-4 w-4" strokeWidth={1.5} />
                    Ativar processo
                </Button>
            )}
            {closings.length > 0 && (
                <AlertDialog open={open} onOpenChange={(next) => { setOpen(next); if (!next) setChoice(null) }}>
                    <AlertDialogTrigger asChild>
                        <Button type="button" variant="outline" size="sm" className="gap-1 border-border text-foreground" disabled={busy}>
                            <Flag className="h-4 w-4" strokeWidth={1.5} />
                            Encerrar processo
                        </Button>
                    </AlertDialogTrigger>
                    <AlertDialogContent className="max-w-lg rounded-card">
                        <AlertDialogHeader>
                            <AlertDialogTitle className="font-display font-normal">
                                Como este processo <span className="font-extrabold text-brand">termina</span>?
                            </AlertDialogTitle>
                            <AlertDialogDescription>
                                Encerrar é definitivo: o processo não volta a ficar ativo. A etapa no quadro continua a mesma.
                            </AlertDialogDescription>
                        </AlertDialogHeader>
                        <div className="flex flex-col gap-2" role="radiogroup" aria-label="Tipo de encerramento">
                            {closings.map(({ status: value, label, description, icon: Icon, tone }) => (
                                <button
                                    key={value}
                                    type="button"
                                    role="radio"
                                    aria-checked={choice === value}
                                    onClick={() => setChoice(value)}
                                    className={cn(
                                        'flex items-start gap-3 rounded-md border-2 p-3 text-left transition-colors',
                                        choice === value ? tone : 'border-border hover:bg-secondary'
                                    )}
                                >
                                    <Icon className="mt-0.5 h-5 w-5 shrink-0" strokeWidth={1.5} />
                                    <span>
                                        <span className="block text-sm font-semibold text-foreground">{label}</span>
                                        <span className="block text-xs text-muted-foreground">{description}</span>
                                    </span>
                                </button>
                            ))}
                        </div>
                        <AlertDialogFooter>
                            <AlertDialogCancel className="rounded-full">Voltar</AlertDialogCancel>
                            <Button
                                type="button"
                                variant={choice === PurchaseProcessStatus.FINISHED ? 'default' : 'destructive'}
                                disabled={!choice || busy}
                                onClick={() => void confirm()}
                            >
                                {selected ? `${selected.label} definitivamente` : 'Escolha uma opção'}
                            </Button>
                        </AlertDialogFooter>
                    </AlertDialogContent>
                </AlertDialog>
            )}
        </div>
    )
}
